import { inject, Injectable } from '@angular/core'
import { BehaviorSubject, combineLatest, Observable } from 'rxjs'
import { ClientIPModalSettings, ClientIPOutsideSettings, ClientIPSettings } from '../../types/ClientIPListSettings'
import { map } from 'rxjs/operators'
import { DateUtils } from '../../../../../../services/date-utils.service'
import { UserSettingsService } from '../../../../../../services/user-settings/user-settings.service'
import {
  isNullableBoolean,
  isOptionalIsoDateString,
  matchesShape,
} from '../../../../../../services/user-settings/user-settings-value-guards'

type StoredClientIPModalSettings = Omit<ClientIPModalSettings, 'dueDate'> & { dueDate?: string }

@Injectable({
  providedIn: 'root',
})
export class ClientIpListSettingsService {
  private userSettingsService = inject(UserSettingsService)

  private static readonly USER_SETTINGS_KEY = 'client-ip-list-settings-modal'

  private static readonly DEFAULT_MODAL_SETTINGS: ClientIPModalSettings = {
    showArchivedPrograms: false,
    showTerminatedProgramsEnrollments: false,
    showArchivedSubprograms: false,
    showTerminatedSubprogramsEnrollments: false,
    showClosedIP: false,
    showCompletedGoals: false,
    showCompletedSteps: false,
    dueDate: undefined,
  }

  private static readonly DEFAULT_STORED_MODAL_SETTINGS: StoredClientIPModalSettings = {
    ...ClientIpListSettingsService.DEFAULT_MODAL_SETTINGS,
    dueDate: undefined,
  }

  private static isValidStoredClientIPModalSettings(value: unknown): value is StoredClientIPModalSettings {
    return matchesShape<StoredClientIPModalSettings>(value, {
      showArchivedPrograms: isNullableBoolean,
      showTerminatedProgramsEnrollments: isNullableBoolean,
      showArchivedSubprograms: isNullableBoolean,
      showTerminatedSubprogramsEnrollments: isNullableBoolean,
      showClosedIP: isNullableBoolean,
      showCompletedGoals: isNullableBoolean,
      showCompletedSteps: isNullableBoolean,
      dueDate: isOptionalIsoDateString,
    })
  }

  readonly defaultModalSettings: ClientIPModalSettings = ClientIpListSettingsService.DEFAULT_MODAL_SETTINGS

  readonly defaultOutsideSettings: ClientIPOutsideSettings = {
    search: '',
  }

  private modalSettingsSubject$$ = new BehaviorSubject<ClientIPModalSettings>(this.defaultModalSettings)
  private outsideSettingsSubject$$ = new BehaviorSubject<ClientIPOutsideSettings>(this.defaultOutsideSettings)

  combinedSettings$: Observable<ClientIPSettings> = combineLatest([
    this.modalSettingsSubject$$.asObservable(),
    this.outsideSettingsSubject$$.asObservable(),
  ]).pipe(
    map(([modalSettings, outsideSettings]) => ({
      ...modalSettings,
      ...outsideSettings,
    })),
  )

  constructor() {
    this.userSettingsService
      .getSetting<StoredClientIPModalSettings>(ClientIpListSettingsService.USER_SETTINGS_KEY, {
        defaultValue: ClientIpListSettingsService.DEFAULT_STORED_MODAL_SETTINGS,
        isValid: ClientIpListSettingsService.isValidStoredClientIPModalSettings,
      })
      .subscribe((stored) => {
        this.modalSettingsSubject$$.next({
          ...this.defaultModalSettings,
          ...stored,
          dueDate: DateUtils.initializeDate(stored.dueDate),
        })
      })
  }

  resetModalSettings() {
    this.modalSettingsSubject$$.next(this.defaultModalSettings)
    void this.persistModalSettings(this.defaultModalSettings)
  }

  emptySettings() {
    const settings = this.modalSettingsSubject$$.value
    for (const key of Object.keys(this.defaultModalSettings) as (keyof ClientIPModalSettings)[]) {
      if (settings.hasOwnProperty(key)) {
        if (settings[key] instanceof Date) {
          const dateSetting = DateUtils.initializeDate(settings[key] as Date)
          const dateDefaultSettings = DateUtils.initializeDate(this.defaultModalSettings[key] as Date)
          if (
            (dateSetting && !dateDefaultSettings) ||
            (!dateSetting && dateDefaultSettings) ||
            (dateSetting && dateDefaultSettings && dateSetting.getTime() !== dateDefaultSettings.getTime())
          ) {
            return false
          }
        } else if (settings[key] !== this.defaultModalSettings[key]) {
          return false // Found difference
        }
      }
    }

    return true
  }

  getModalSettings() {
    return this.modalSettingsSubject$$.value
  }

  patchModalSettings(settings: Partial<ClientIPModalSettings>) {
    const { dueDate, ...rest } = settings
    const updated: ClientIPModalSettings = {
      ...this.getModalSettings(),
      ...rest,
      dueDate: DateUtils.initializeDate(dueDate),
    }
    this.modalSettingsSubject$$.next(updated)
    void this.persistModalSettings(updated)
  }

  patchOutsideSettings(settings: Partial<ClientIPOutsideSettings>) {
    this.outsideSettingsSubject$$.next({ ...this.outsideSettingsSubject$$.value, ...settings })
  }

  getSettingsAsObservable() {
    return this.combinedSettings$
  }

  private persistModalSettings(settings: ClientIPModalSettings): Promise<void> {
    const stored: StoredClientIPModalSettings = {
      ...settings,
      dueDate: settings.dueDate ? DateUtils.toIso(settings.dueDate) : undefined,
    }
    return this.userSettingsService.patchSetting(ClientIpListSettingsService.USER_SETTINGS_KEY, stored)
  }
}
